import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import { Modal, FormError, SubmitBar, ConfirmDialog } from './admin-ui';

/**
 * Slice 7.7 — per-asset threat composition selection panel.
 *
 * Modal launched from the AssetGrid Threat cell. Lists candidate
 * compositions (everything matching the asset's asset_type), with a
 * checkbox per row. Saving sends a replace-all PUT to the backend, which
 * sets threat_selection_status to 'Reviewed'.
 *
 * "Restore defaults" wipes the current selection and re-applies all
 * system-managed compositions matching the asset_type. This puts the
 * asset back to status='Defaults' and is the safe escape hatch if the
 * user needs to start over.
 *
 * Visual structure matches the legacy ThreatAnalysisModal: two groups,
 * "Currently included" (shown first, checked) and "Available, not
 * included" (shown second, unchecked). Sort within each group by
 * legacy_code to match the catalog admin browse order.
 */

interface CandidateRow {
  composition_id: string;
  legacy_code: string | null;
  is_system_managed: number;
  threat_event_name: string;
  threat_event_legacy_code: string | null;
  vulnerability_name: string;
  vulnerability_legacy_code: string | null;
  vulnerability_severity: string;
  predispos_name: string;
  predispos_legacy_code: string | null;
  predispos_pervasiveness: string;
  impact_name: string;
  impact_legacy_code: string | null;
  impact_level: string;
  is_included: number;
  inclusion_source: 'Default' | 'Customer' | null;
}

interface SelectionResponse {
  asset_id: string;
  asset_name: string;
  asset_type_id: string;
  asset_type_name: string;
  threat_selection_status: 'Defaults' | 'Reviewed' | 'Custom';
  candidates: CandidateRow[];
}

export function ThreatSelectionPanel({ open, onClose, assetId, projectId }: {
  open: boolean;
  onClose: () => void;
  assetId: string | null;
  projectId: string;  // for invalidating the AssetGrid query after save
}) {
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['asset', assetId, 'threat-compositions'],
    queryFn: () => api.get<SelectionResponse>(`/assets/${assetId}/threat-compositions`),
    enabled: open && !!assetId,
  });

  const [includedSet, setIncludedSet] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);

  // When data loads, seed the checked set from the server-side `is_included`
  useEffect(() => {
    if (!q.data) return;
    const seed = new Set<string>();
    for (const c of q.data.candidates) {
      if (c.is_included) seed.add(c.composition_id);
    }
    setIncludedSet(seed);
    setError(null);
  }, [q.data]);

  function toggle(id: string) {
    setIncludedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Group candidates: included first, then not-included. Within each
  // group, sort by legacy_code with nulls last.
  const grouped = useMemo(() => {
    if (!q.data) return { included: [] as CandidateRow[], excluded: [] as CandidateRow[] };
    const f = filter.trim().toLowerCase();
    const matches = (c: CandidateRow) =>
      !f ||
      (c.legacy_code?.toLowerCase().includes(f) ?? false) ||
      c.threat_event_name.toLowerCase().includes(f) ||
      c.vulnerability_name.toLowerCase().includes(f);

    const included: CandidateRow[] = [];
    const excluded: CandidateRow[] = [];
    for (const c of q.data.candidates) {
      if (!matches(c)) continue;
      if (includedSet.has(c.composition_id)) included.push(c);
      else excluded.push(c);
    }
    const cmp = (a: CandidateRow, b: CandidateRow) => {
      const ac = a.legacy_code ?? '\uFFFF';
      const bc = b.legacy_code ?? '\uFFFF';
      return ac.localeCompare(bc);
    };
    included.sort(cmp);
    excluded.sort(cmp);
    return { included, excluded };
  }, [q.data, includedSet, filter]);

  async function handleSave() {
    if (!assetId) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.put(`/assets/${assetId}/threat-compositions`, {
        included_ids: Array.from(includedSet),
      });
      // Invalidate the asset detail (so status flips to 'Reviewed' in the grid)
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'assets', 'detailed'] });
      qc.invalidateQueries({ queryKey: ['asset', assetId, 'threat-compositions'] });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRestoreDefaults() {
    if (!assetId) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/assets/${assetId}/threat-compositions/restore-defaults`, {});
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'assets', 'detailed'] });
      qc.invalidateQueries({ queryKey: ['asset', assetId, 'threat-compositions'] });
      setConfirmRestore(false);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
      setConfirmRestore(false);
    } finally {
      setSubmitting(false);
    }
  }

  const title = q.data ? `Threat compositions — ${q.data.asset_name}` : 'Threat compositions';
  const total = q.data?.candidates.length ?? 0;
  const includedCount = includedSet.size;

  return (
    <Modal open={open} onClose={onClose} title={title} widthClass="max-w-4xl w-full">
      <FormError message={error} />

      {q.isLoading && <div className="text-sm text-ink-500 italic">Loading…</div>}
      {q.isError && (
        <div className="text-sm text-red-700">
          Failed to load: {q.error instanceof Error ? q.error.message : 'unknown error'}
        </div>
      )}

      {q.data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4 text-xs">
            <div className="border border-ink-200 rounded-sm px-3 py-2">
              <div className="text-ink-500 uppercase tracking-wide text-[10px]">Asset type</div>
              <div className="text-ink-700 font-medium">{q.data.asset_type_name}</div>
            </div>
            <div className="border border-ink-200 rounded-sm px-3 py-2">
              <div className="text-ink-500 uppercase tracking-wide text-[10px]">Status</div>
              <div className="text-ink-700 font-medium">
                {q.data.threat_selection_status}
                {q.data.threat_selection_status === 'Defaults' && (
                  <span className="ml-2 text-amber-700 text-[10px]">⚠ unreviewed</span>
                )}
              </div>
            </div>
            <div className="border border-ink-200 rounded-sm px-3 py-2">
              <div className="text-ink-500 uppercase tracking-wide text-[10px]">Selected</div>
              <div className="text-ink-700 font-medium">
                {includedCount} of {total} compositions
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 mb-2">
            <input
              type="text"
              placeholder="Filter by code, event, vulnerability..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-ink-200 rounded-sm px-3 py-1 text-sm flex-1 max-w-[400px] focus:outline-none focus:border-accent"
            />
            <button
              type="button"
              onClick={() => setConfirmRestore(true)}
              disabled={submitting}
              className="text-xs text-ink-600 hover:text-ink-900 underline disabled:opacity-50"
            >
              Restore defaults
            </button>
          </div>

          <div className="border border-ink-200 rounded-sm bg-white max-h-[480px] overflow-auto">
            {/*
              Slice 7.9: rebuilt as a real <table> with <thead> + alternating
              row backgrounds + hover + explicit column widths. The previous
              grid-template implementation crammed all columns into one
              fixed-width row with the Impact column tucked at the end —
              hard to scan and missed the visual model of the catalog admin
              browse pages, which this should match.
            */}
            <table className="w-full text-xs" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '36px' }} />
                <col style={{ width: '90px' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '90px' }} />
              </colgroup>
              <thead className="bg-ink-50 sticky top-0 z-10 border-b border-ink-200">
                <tr>
                  <th className="text-left px-2 py-2"></th>
                  <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wide text-ink-500">Code</th>
                  <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wide text-ink-500">Threat event</th>
                  <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wide text-ink-500">Vulnerability</th>
                  <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wide text-ink-500">Predisposing condition</th>
                  <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wide text-ink-500">Impact</th>
                  <th className="text-right px-2 py-2 text-[10px] uppercase tracking-wide text-ink-500">Severity</th>
                </tr>
              </thead>
              <tbody>
                <CandidateGroup
                  label={`Currently included (${grouped.included.length})`}
                  candidates={grouped.included}
                  isChecked={(id) => includedSet.has(id)}
                  onToggle={toggle}
                  groupKey="included"
                />
                {grouped.included.length > 0 && grouped.excluded.length > 0 && (
                  <tr><td colSpan={7} className="border-t-2 border-ink-200" /></tr>
                )}
                <CandidateGroup
                  label={`Available, not included (${grouped.excluded.length})`}
                  candidates={grouped.excluded}
                  isChecked={(id) => includedSet.has(id)}
                  onToggle={toggle}
                  groupKey="excluded"
                />
              </tbody>
            </table>
          </div>

          <SubmitBar
            onCancel={onClose}
            onSubmit={handleSave}
            submitting={submitting}
            submitLabel={`Save (${includedCount} selected)`}
          />
        </>
      )}

      <ConfirmDialog
        open={confirmRestore}
        title="Restore default threat selections"
        message="This wipes any custom selections for this asset and re-applies all system-managed compositions matching the asset type. The asset's status will go back to 'Defaults' and you'll be prompted to review again before the next assessment run."
        confirmLabel="Restore defaults"
        onConfirm={handleRestoreDefaults}
        onCancel={() => setConfirmRestore(false)}
      />
    </Modal>
  );
}

function CandidateGroup({ label, candidates, isChecked, onToggle, groupKey }: {
  label: string;
  candidates: CandidateRow[];
  isChecked: (id: string) => boolean;
  onToggle: (id: string) => void;
  groupKey: 'included' | 'excluded';
}) {
  return (
    <>
      {/* Group header — sticky inside the scroll area, spans all columns */}
      <tr className="sticky top-[33px] z-[5] bg-ink-100 border-b border-ink-200">
        <td colSpan={7} className="px-2 py-1 text-[10px] uppercase tracking-wide text-ink-600 font-semibold">
          {label}
        </td>
      </tr>
      {candidates.length === 0 ? (
        <tr>
          <td colSpan={7} className="text-xs text-ink-400 italic px-3 py-3 text-center">
            none
          </td>
        </tr>
      ) : (
        candidates.map((c, i) => {
          // Slice 7.9: alternating-row striping based on position within the
          // group + hover affordance. Helps scanning long candidate lists,
          // especially in the catalog admin's high-cardinality views.
          const stripe = i % 2 === 0 ? 'bg-white' : 'bg-ink-50/40';
          return (
            <tr
              key={`${groupKey}-${c.composition_id}`}
              className={`${stripe} hover:bg-amber-50/60 cursor-pointer border-b border-ink-100 last:border-b-0`}
              onClick={() => onToggle(c.composition_id)}
            >
              <td className="px-2 py-1.5">
                <input
                  type="checkbox"
                  checked={isChecked(c.composition_id)}
                  onChange={() => onToggle(c.composition_id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </td>
              <td className="px-2 py-1.5 font-mono text-ink-500 text-[11px] truncate" title={c.legacy_code ?? ''}>
                {c.legacy_code ?? '—'}
              </td>
              <td className="px-2 py-1.5 text-ink-800 truncate" title={c.threat_event_name}>
                {c.threat_event_name}
              </td>
              <td className="px-2 py-1.5 text-ink-700 truncate" title={c.vulnerability_name}>
                {c.vulnerability_name}
              </td>
              <td className="px-2 py-1.5 text-ink-600 truncate" title={c.predispos_name}>
                {c.predispos_name}
              </td>
              <td className="px-2 py-1.5 text-ink-600 truncate" title={c.impact_name}>
                {c.impact_name}
              </td>
              <td className="px-2 py-1.5 text-right">
                <ImpactBadge level={c.impact_level} />
              </td>
            </tr>
          );
        })
      )}
    </>
  );
}

/**
 * Slice 7.9: small badge for the impact level. Maps the categorical
 * Likert-ish levels to a pill color so the user can scan severity quickly.
 */
function ImpactBadge({ level }: { level: string }) {
  const norm = level.toLowerCase();
  const cls =
    norm.includes('very_high') || norm.includes('critical') ? 'bg-red-100 text-red-800 border-red-200'
    : norm.includes('high')     ? 'bg-orange-100 text-orange-800 border-orange-200'
    : norm.includes('moderate') ? 'bg-amber-100 text-amber-800 border-amber-200'
    : norm.includes('low')      ? 'bg-green-100 text-green-800 border-green-200'
    : 'bg-ink-100 text-ink-700 border-ink-200';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {level.replace('_', ' ')}
    </span>
  );
}
